import React, { useCallback, useEffect, useState } from "react";
import {
  InboxArrowDownIcon,
  ChatBubbleLeftRightIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { getQueue, getMyChats, claimChat } from "../api/operator";
import ChatListPanel from "./Operator/ChatListPanel";
import Conversation from "./Operator/Conversation";
import UserProfileDrawer from "./Operator/UserProfileDrawer";
import StatsPanel from "./Operator/StatsPanel";
import UserSearchModal from "./Operator/UserSearchModal";
import ChatFilterBar from "./Operator/ChatFilterBar";
import ThemeSwitch from "./ThemeSwitch";
import LanguageSwitch from "./LanguageSwitch";
import { useT } from "../contexts/LanguageContext";

const TABS = [
  { id: "queue", labelKey: "tab.queue", icon: InboxArrowDownIcon },
  { id: "mine", labelKey: "tab.mine", icon: ChatBubbleLeftRightIcon },
  { id: "closed", labelKey: "tab.closed", icon: ArchiveBoxIcon },
  { id: "stats", labelKey: "tab.stats", icon: ChartBarIcon },
];

const OperatorDashboardNew = ({ operatorUser }) => {
  const t = useT();
  // Бет жаңартылғанда ашық чат пен бөлім жоғалып кетпеуі керек еді — бұрын
  // әр F5-тен кейін чатты қайта іздеп табуға тура келетін.
  const [tab, setTab] = useState(() => {
    try {
      return localStorage.getItem("operator_tab") || "queue";
    } catch {
      return "queue";
    }
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [queue, setQueue] = useState([]);
  const [mine, setMine] = useState([]);
  const [closed, setClosed] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activeChatId, setActiveChatId] = useState(() => {
    try {
      const v = localStorage.getItem("operator_active_chat");
      return v ? Number(v) : null;
    } catch {
      return null;
    }
  });
  const [showProfile, setShowProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showUserSearch, setShowUserSearch] = useState(false);

  // filters
  useEffect(() => {
    try {
      if (activeChatId) localStorage.setItem("operator_active_chat", String(activeChatId));
      else localStorage.removeItem("operator_active_chat");
    } catch {
      /* жеке терезеде localStorage жабық болуы мүмкін — маңызды емес */
    }
  }, [activeChatId]);

  useEffect(() => {
    try {
      localStorage.setItem("operator_tab", tab);
    } catch {
      /* жоғарыдағыдай */
    }
  }, [tab]);

  const [searchQuery, setSearchQuery] = useState("");
  const [priority, setPriority] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const trimmed = searchQuery.trim() || undefined;
      if (tab === "queue") {
        const data = await getQueue({ q: trimmed, has_unread: hasUnread || undefined });
        setQueue(data.items || []);
      } else if (tab === "mine") {
        const data = await getMyChats({
          status: "open",
          limit: 50,
          q: trimmed,
          priority: priority || undefined,
          has_unread: hasUnread || undefined,
        });
        setMine(data.items || []);
      } else if (tab === "closed") {
        const data = await getMyChats({
          status: "closed",
          limit: 50,
          q: trimmed,
          priority: priority || undefined,
        });
        setClosed(data.items || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab, searchQuery, priority, hasUnread]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // periodic refresh
  useEffect(() => {
    const id = setInterval(() => refresh(), 20000);
    return () => clearInterval(id);
  }, [refresh]);

  const handleClaim = async (chatId) => {
    try {
      await claimChat(chatId, "normal");
      setTab("mine");
      setActiveChatId(chatId);
      setQueue((prev) => prev.filter((c) => c.chat_id !== chatId));
    } catch (e) {
      alert(`${t("list.claimFailed")}: ${e.message}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("google_id_token");
    window.location.href = "/";
  };

  const currentList = tab === "queue" ? queue : tab === "mine" ? mine : tab === "closed" ? closed : [];

  return (
    <div className="flex h-screen bg-bg-base overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 dark-sidebar transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative lg:flex-shrink-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-accent-grad rounded-xl flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">{t("app.support")}</h1>
                <p className="text-xs text-text-secondary">{t("app.operatorPanel")}</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-text-secondary"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 p-3 space-y-1">
            {TABS.map(({ id, labelKey, icon: Icon }) => (
              <button
                key={id}
                onClick={() => {
                  setTab(id);
                  setActiveChatId(null);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg ${
                  tab === id
                    ? "bg-accent-grad text-white"
                    : "text-text-secondary hover:bg-bg-primary hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{t(labelKey)}</span>
              </button>
            ))}
          </nav>

          <div className="p-3 border-t border-gray-700 space-y-2">
            <div className="flex items-center justify-between px-2">
              <span className="text-xs text-text-secondary">{t("app.language")}</span>
              <LanguageSwitch />
            </div>
            <div className="flex items-center justify-between px-2">
              <span className="text-xs text-text-secondary">{t("app.theme")}</span>
              <ThemeSwitch />
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-text-secondary hover:bg-red-500/10 hover:text-red-400"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              <span className="text-sm">{t("app.logout")}</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {tab === "stats" ? (
        <div className="flex-1 overflow-y-auto">
          <header className="bg-bg-card border-b border-border-primary p-3 flex items-center gap-3 lg:hidden">
            <button onClick={() => setSidebarOpen(true)} className="text-white">
              <Bars3Icon className="w-6 h-6" />
            </button>
            <span className="text-white font-semibold">{t("tab.stats")}</span>
          </header>
          <StatsPanel />
        </div>
      ) : (
        <>
          {/* Chat list */}
          <div
            className={`${
              activeChatId ? "hidden md:flex" : "flex"
            } flex-col w-full md:w-80 lg:w-96 bg-bg-card border-r border-border-primary`}
          >
            <header className="p-3 border-b border-gray-700 flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-white">
                <Bars3Icon className="w-6 h-6" />
              </button>
              <span className="text-white font-semibold">
                {t(TABS.find((x) => x.id === tab)?.labelKey)}
              </span>
              <span className="text-xs text-text-secondary ml-auto">
                {currentList.length}
              </span>
              <button onClick={refresh} className="text-text-secondary">
                <ArrowPathIcon
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
              </button>
            </header>
            <ChatFilterBar
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              priority={priority}
              onPriorityChange={setPriority}
              hasUnread={hasUnread}
              onHasUnreadChange={setHasUnread}
              onOpenSearch={() => setShowUserSearch(true)}
              showPriority={tab !== "queue"}
            />
            <ChatListPanel
              chats={currentList}
              activeChatId={activeChatId}
              onSelect={setActiveChatId}
              loading={loading}
              emptyText={
                tab === "queue"
                  ? t("list.emptyQueue")
                  : tab === "mine"
                  ? t("list.emptyMine")
                  : t("list.emptyClosed")
              }
              getAction={
                tab === "queue"
                  ? (c) => (
                      <button
                        onClick={() => handleClaim(c.chat_id)}
                        className="px-2.5 py-1 bg-green-600 text-white text-xs rounded-lg"
                      >
                        {t("list.claim")}
                      </button>
                    )
                  : null
              }
            />
          </div>

          {/* Conversation */}
          <div className={`${activeChatId ? "flex" : "hidden md:flex"} flex-1 flex-col`}>
            {activeChatId && (
              <div className="md:hidden p-2 bg-bg-card border-b border-border-primary">
                <button
                  onClick={() => setActiveChatId(null)}
                  className="text-white text-sm"
                >
                  ← {t("app.back")}
                </button>
              </div>
            )}
            <Conversation
              chatId={activeChatId}
              operatorUser={operatorUser}
              onChatChanged={refresh}
              onUserProfile={(p) => {
                setProfile(p);
                setShowProfile(true);
              }}
            />
          </div>

          {/* Profile drawer (desktop side, mobile overlay) */}
          {showProfile && profile && (
            <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setShowProfile(false)}>
              <div onClick={(e) => e.stopPropagation()} className="absolute right-0 top-0 bottom-0">
                <UserProfileDrawer profile={profile} onClose={() => setShowProfile(false)} />
              </div>
            </div>
          )}
          {showProfile && profile && (
            <div className="hidden lg:block">
              <UserProfileDrawer profile={profile} onClose={() => setShowProfile(false)} />
            </div>
          )}
        </>
      )}

      {showUserSearch && (
        <UserSearchModal
          onClose={() => setShowUserSearch(false)}
          onChatStarted={(chatId) => {
            setTab("mine");
            setActiveChatId(chatId);
            refresh();
          }}
        />
      )}
    </div>
  );
};

export default OperatorDashboardNew;
